import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiClient from './../api/apiClient';
import { API_URLS } from './../api/apiUrls';

export const useApi = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const buildUrl = useCallback((key, params = {}) => {
        // Si la key ya es una ruta (contiene "/") la devolvemos tal cual
        if (typeof key === "string" && (key.startsWith("/") || key.startsWith("http") || key.includes("/"))) {
            return key;
        }

        const urlOrFunc = API_URLS[key];
        if (urlOrFunc === undefined) {
            throw new Error(`API_URLS no tiene la clave '${key}'`);
        }

        if (typeof urlOrFunc === "function") {
            const urlParams = params.urlParams;

            // Si pasan un array, lo expandimos en orden
            if (Array.isArray(urlParams)) {
                return urlOrFunc(...urlParams);
            }

            // Si pasan un objeto, usamos sus valores en orden
            if (urlParams && typeof urlParams === "object") {
                return urlOrFunc(...Object.values(urlParams));
            }

            // Compatibilidad con uso previo: params.id o params.urlParams.id
            const id = params.urlParams?.id ?? params.id;
            return urlOrFunc(id, params);
        }

        return urlOrFunc;
    }, []);



    const baseRequest = useCallback(async ({
        method,
        urlKey,
        urlParams = {},
        data: body = null,
        headers = {},
        params = null,
        successMessage,
        errorMessage = 'Ocurrió un error',
        showSuccess = false,
        showError = true,
        ...options
    }) => {
        setLoading(true);
        setError(null);

        try {
            const url = buildUrl(urlKey, urlParams);
            const response = await apiClient({
                method,
                url,
                data: body,
                params,
                headers,
                ...options
            });

            setData(response.data);

            if (showSuccess && successMessage) {
                toast.success(successMessage);
            }

            return response.data;
        } catch (err) {
            setError(err);

            if (showError) {
                const msg = err.response?.data?.message || errorMessage;
                toast.error(msg);
            }

            throw err;
        } finally {
            setLoading(false);
        }
    }, [buildUrl]);

    const get = useCallback((urlKey, options = {}) => baseRequest({ method: 'get', urlKey, ...options }), [baseRequest]);
    const post = useCallback((urlKey, data, options = {}) => baseRequest({ method: 'post', urlKey, data, ...options }), [baseRequest]);
    const put = useCallback((urlKey, data, options = {}) => baseRequest({ method: 'put', urlKey, data, ...options }), [baseRequest]);
    const patch = useCallback((urlKey, data, options = {}) => baseRequest({ method: 'patch', urlKey, data, ...options }), [baseRequest]);
    const del = useCallback((urlKey, options = {}) => baseRequest({ method: 'delete', urlKey, ...options }), [baseRequest]);

    return {
        loading,
        error,
        data,
        get,
        post,
        put,
        patch,
        del,
    };
};
